import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { alpha, Box, Stack, type SvgIconProps } from "@mui/material";
import { type ReactElement } from "react";

import { Icon, type IconType } from "../../components/Icon";
import { Text } from "../../components/Text";
import {
  getBackgroundMuted,
  getBackgroundStandout,
  getBorderBase,
  getMutedText,
  getStrongText,
} from "../themeHelpers";

export const HELP_CENTER_CARD_TONES = ["light", "dark"] as const;
export type HelpCenterCardTone = (typeof HELP_CENTER_CARD_TONES)[number];

interface HelpCenterActionCardProps {
  title: string;
  description: string;
  href: string;
  isMobile?: boolean;
  iconType?: IconType;
  icon?: ReactElement<SvgIconProps>;
  target?: string;
  rel?: string;
  onClick?: () => void;
  tone?: HelpCenterCardTone;
}

function getActionIcon(icon?: ReactElement<SvgIconProps>, iconType?: IconType): ReactElement {
  if (icon) {
    return icon;
  }

  if (iconType) {
    return <Icon size="medium" type={iconType} />;
  }

  return <PhoneOutlinedIcon fontSize="small" />;
}

export function HelpCenterActionCard(props: HelpCenterActionCardProps) {
  const {
    title,
    description,
    href,
    icon,
    iconType,
    isMobile = false,
    target,
    rel,
    onClick,
    tone = "light",
  } = props;
  const isDark = tone === "dark";

  return (
    <Box
      component="a"
      href={href}
      target={target}
      rel={rel}
      sx={(theme) => ({
        display: "block",
        width: "100%",
        textDecoration: "none",
        borderRadius: theme.borderRadius?.xLarge,
        border: `1px solid ${getBorderBase(theme)}`,
        backgroundColor: theme.palette.background.paper,
        paddingTop: theme.spacing(isMobile ? 5 : 4),
        paddingRight: theme.spacing(isMobile ? 5 : 4),
        paddingBottom: theme.spacing(isMobile ? 5 : 4),
        paddingLeft: theme.spacing(isMobile ? 5 : 4),
        minHeight: isMobile ? "6rem" : undefined,
        boxShadow: theme.shadow?.card,
        transition: theme.transitions.create(
          ["background-color", "border-color", "transform", "box-shadow"],
          {
            duration: theme.transitions.duration.shorter,
          }
        ),
        "&:hover": {
          backgroundColor: getBackgroundStandout(theme),
          borderColor: getBorderBase(theme),
          transform: "translateY(-1px)",
          boxShadow: `0 0.75rem 1.75rem ${alpha(getStrongText(theme), isDark ? 0.07 : 0.05)}`,
        },
        "&:focus-visible": {
          outline: `2px solid ${
            isDark
              ? alpha(theme.palette.primary.main, 0.22)
              : alpha(theme.palette.primary.main, 0.6)
          }`,
          outlineOffset: "2px",
        },
      })}
      onClick={onClick}
    >
      <Stack alignItems="flex-start" direction="row" spacing={2}>
        <Box
          sx={(theme) => ({
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: isMobile ? "2.75rem" : "3rem",
            height: isMobile ? "2.75rem" : "3rem",
            minWidth: isMobile ? "2.75rem" : "3rem",
            borderRadius: theme.borderRadius?.large,
            backgroundColor: getBackgroundMuted(theme),
            color: getStrongText(theme),
            border: `1px solid ${getBorderBase(theme)}`,
          })}
        >
          {getActionIcon(icon, iconType)}
        </Box>

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Text
            bold
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.text.primary,
              lineHeight: 1.2,
              fontSize: theme.typography.h6.fontSize,
              fontFamily: theme.typography.body1.fontFamily,
            })}
          >
            {title}
          </Text>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: getMutedText(theme),
              maxWidth: isMobile ? undefined : "12rem",
            })}
          >
            {description}
          </Text>
        </Stack>

        <Icon
          size="small"
          sx={(theme) => ({
            color: getMutedText(theme),
            marginTop: isMobile ? "0.5rem" : "0.375rem",
          })}
          type="chevron-right"
        />
      </Stack>
    </Box>
  );
}
